import { useEffect, useMemo, useState } from "react";
import { TriangleAlert } from "lucide-react";

import { getCarteira } from "services/carteira/carteira.service";
import { pesoCubado } from "utils/caixa";
import type { Estagio1Data } from "./Estagio1";

type Props = {
  estagio1: Estagio1Data;
  /** Quando existe, mostra o destino já escolhido no Estágio 2. */
  destino?: { cidade?: string; estado?: string; pais?: string };
  /** Linha extra do Estágio 3: transportadora escolhida e total. */
  frete?: { carrier?: string; totalLabel?: string; total?: string };
  onAdicionarSaldo?: () => void;
};

const n = (v: unknown) => {
  const x = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(x) ? x : 0;
};

const kg = (v: number) =>
  `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} kg`;

/**
 * Painel de apoio das três etapas da cotação.
 *
 * Vive no wrapper, não dentro de um estágio: ele é o único que enxerga o
 * Estágio 1 e o 2 ao mesmo tempo, então o resumo não precisa ser remontado em
 * cada tela nem passado de mão em mão.
 */
const ResumoCotacao = ({ estagio1, destino, frete, onAdicionarSaldo }: Props) => {
  const [saldo, setSaldo] = useState<number | null>(null);

  // Só a allowlist da carteira tem cobrança antecipada; fora dela o aviso de
  // saldo não faz sentido e o painel simplesmente não o mostra.
  useEffect(() => {
    getCarteira()
      .then((r) => setSaldo(r.ok && r.habilitada ? r.saldo : null))
      .catch(() => setSaldo(null));
  }, []);

  const dados = useMemo(() => {
    const lista = estagio1.caixas ?? [];

    // Fluxo manual: uma linha por volume, cada uma com a sua caixa.
    if (lista.length) {
      const real = lista.reduce((acc, c) => acc + n(c.pesoKg), 0);
      const cubado = lista.reduce(
        (acc, c) =>
          acc +
          (c.caixa
            ? pesoCubado(n(c.caixa.comprimento), n(c.caixa.largura), n(c.caixa.altura))
            : 0),
        0,
      );
      const itens = lista.reduce((acc, c) => acc + (c.itens?.length ?? 0), 0);
      const valor = lista.reduce(
        (acc, c) =>
          acc + (c.itens ?? []).reduce((s, i) => s + n(i.precoUnit) * n(i.qty), 0),
        0,
      );

      return { volumes: lista.length, real, cubado, itens, valor, tipos: itens };
    }

    // Fluxo com pedido importado: uma caixa só.
    const caixa = estagio1.selectedCaixa;
    const cubado = caixa
      ? pesoCubado(n(caixa.comprimento), n(caixa.largura), n(caixa.altura))
      : 0;
    const produtos = n(estagio1.pesoTotal);
    const tara = n(caixa?.peso);
    const itens = estagio1.selectedPedido?.itens?.length ?? 0;
    const valor = n(estagio1.selectedPedido?.total);

    return {
      volumes: caixa ? 1 : 0,
      real: produtos + tara,
      cubado,
      itens,
      valor,
      tipos: itens,
    };
  }, [estagio1]);

  const taxavel = Math.max(dados.real, dados.cubado);
  const maior = Math.max(dados.real, dados.cubado, 0.001);

  const caixaRotulo = (() => {
    const c = estagio1.caixas?.[0]?.caixa ?? estagio1.selectedCaixa;
    if (!c) return null;
    return `${c.cod_identificacao} · ${n(c.comprimento)}×${n(c.largura)}×${n(c.altura)}`;
  })();

  const destinoRotulo = [destino?.cidade, destino?.estado, destino?.pais]
    .filter(Boolean)
    .join(", ");

  return (
    <aside className="flex flex-col gap-4 border-slate-100 bg-slate-50/60 px-5 py-5 md:border-l">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        Resumo
      </p>

      <dl className="space-y-1.5 text-xs">
        {estagio1.pedidoId && <Linha termo="ID do envio" valor={`#${estagio1.pedidoId}`} />}
        {destinoRotulo && <Linha termo="Destino" valor={destinoRotulo} />}
        <Linha termo="Volumes" valor={String(dados.volumes || "—")} />
        <Linha termo="Peso total" valor={dados.real ? kg(dados.real) : "—"} />
        {caixaRotulo && <Linha termo="Caixa" valor={caixaRotulo} />}
        <Linha
          termo="Itens"
          valor={dados.itens ? `${dados.itens} ${dados.itens === 1 ? "tipo" : "tipos"}` : "—"}
        />
        <Linha
          termo="Valor declarado"
          valor={
            dados.valor
              ? `${estagio1.currencyPrice} ${dados.valor.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "—"
          }
        />
        {frete?.carrier && <Linha termo="Frete" valor={frete.carrier} />}
      </dl>

      {/* Peso cobrado: a transportadora usa o maior entre real e cubado, e
          quem não sabe disso embala em caixa grande e paga por ar. */}
      {dados.volumes > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
          <p className="text-[10px] text-slate-400">Peso cobrado pela transportadora</p>
          <p className="mt-0.5 text-2xl font-bold leading-none text-slate-900">
            {kg(taxavel)}
          </p>

          <div className="mt-2.5 space-y-1.5">
            <Barra rotulo="Real" valor={dados.real} maximo={maior} destacado={dados.real >= dados.cubado} />
            <Barra rotulo="Cubado" valor={dados.cubado} maximo={maior} destacado={dados.cubado > dados.real} />
          </div>

          <p className="mt-2 text-[10px] leading-snug text-slate-400">
            Vale o maior dos dois. Caixas menores podem baratear o frete.
          </p>
        </div>
      )}

      {frete?.total && (
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
          <p className="text-[10px] text-slate-400">{frete.totalLabel ?? "Total a pagar"}</p>
          <p className="mt-0.5 text-xl font-bold leading-none text-slate-900">
            {frete.total}
          </p>
        </div>
      )}

      {saldo !== null && saldo <= 0 && (
        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <p className="text-[11px] leading-snug text-amber-900">
            Saldo de R$ 0,00.{" "}
            {onAdicionarSaldo ? (
              <button
                type="button"
                onClick={onAdicionarSaldo}
                className="font-semibold underline underline-offset-2"
              >
                Adicione saldo
              </button>
            ) : (
              <strong>Adicione saldo</strong>
            )}{" "}
            para emitir a etiqueta.
          </p>
        </div>
      )}
    </aside>
  );
};

export default ResumoCotacao;

const Linha = ({ termo, valor }: { termo: string; valor: string }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="shrink-0 text-slate-500">{termo}</dt>
    <dd className="truncate text-right font-medium text-slate-800">{valor}</dd>
  </div>
);

const Barra = ({
  rotulo,
  valor,
  maximo,
  destacado,
}: {
  rotulo: string;
  valor: number;
  maximo: number;
  destacado: boolean;
}) => (
  <div className="flex items-center gap-2">
    <span className="w-12 shrink-0 text-[10px] text-slate-400">{rotulo}</span>
    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
      <span
        className={`block h-full rounded-full ${
          destacado ? "bg-brand-sidebar" : "bg-brand-secondary"
        }`}
        style={{ width: `${Math.min(100, (valor / maximo) * 100)}%` }}
      />
    </span>
    <span className="w-10 shrink-0 text-right text-[10px] tabular-nums text-slate-500">
      {valor.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
    </span>
  </div>
);
